import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import type { Bounds2D } from '@/utils/bounds';

const coordinate = z.number().int().min(-16383).max(16384);
export const CircuitLabelsSchema = z
  .object({
    tags: z.array(
      z.object({
        id: z.string(),
        name: z.string().trim().min(1).max(80),
        color: z.string().regex(/^#[0-9a-f]{6}$/i),
      }),
    ),
    sections: z.array(
      z.object({
        id: z.string(),
        bounds: z
          .object({
            left: coordinate,
            right: coordinate,
            top: coordinate,
            bottom: coordinate,
          })
          .refine((b) => b.left <= b.right && b.top <= b.bottom),
        tagIds: z.array(z.string()).min(1),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    const ids = new Set(data.tags.map((t) => t.id));
    if (
      ids.size !== data.tags.length ||
      new Set(data.sections.map((s) => s.id)).size !== data.sections.length ||
      data.sections.some(
        (s) =>
          s.tagIds.some((id) => !ids.has(id)) ||
          new Set(s.tagIds).size !== s.tagIds.length,
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Label identifiers must be unique and reference existing tags',
      });
    }
  });
export type CircuitLabelsDto = z.infer<typeof CircuitLabelsSchema>;
export type CircuitLabelTag = CircuitLabelsDto['tags'][number];

export class CircuitLabelsModel {
  tags: CircuitLabelsDto['tags'] = [];
  sections: CircuitLabelsDto['sections'] = [];
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
  get dto(): CircuitLabelsDto {
    return {
      tags: this.tags.map((t) => ({ ...t })),
      sections: this.sections.map((s) => ({
        ...s,
        bounds: { ...s.bounds },
        tagIds: [...s.tagIds],
      })),
    };
  }
  load(dto: CircuitLabelsDto) {
    const parsed = CircuitLabelsSchema.parse(dto);
    this.tags = parsed.tags;
    this.sections = parsed.sections;
  }
  addTag(name: string, color: string): string {
    const tag = { id: crypto.randomUUID(), name: name.trim(), color };
    this.load({ ...this.dto, tags: [...this.dto.tags, tag] });
    return tag.id;
  }
  updateTag(id: string, name: string, color: string) {
    this.load({
      ...this.dto,
      tags: this.tags.map((t) => (t.id === id ? { ...t, name: name.trim(), color } : t)),
    });
  }
  removeTag(id: string) {
    this.load({
      tags: this.tags.filter((t) => t.id !== id),
      sections: this.sections
        .map((s) => ({ ...s, tagIds: s.tagIds.filter((t) => t !== id) }))
        .filter((s) => s.tagIds.length),
    });
  }
  setSection(bounds: Bounds2D, tagIds: string[]) {
    const existing = this.sections.find(
      (s) =>
        s.bounds.left === bounds.left &&
        s.bounds.right === bounds.right &&
        s.bounds.top === bounds.top &&
        s.bounds.bottom === bounds.bottom,
    );
    const section = {
      id: existing?.id ?? crypto.randomUUID(),
      bounds,
      tagIds: [...new Set(tagIds)],
    };
    this.load({
      ...this.dto,
      sections: [...this.sections.filter((s) => s !== existing), section],
    });
  }
  /** Add assignments without replacing tags on the same rectangle or any other region. */
  addTagsToSection(bounds: Bounds2D, tagIds: string[]) {
    const existing = this.sections.find(
      (s) =>
        s.bounds.left === bounds.left &&
        s.bounds.right === bounds.right &&
        s.bounds.top === bounds.top &&
        s.bounds.bottom === bounds.bottom,
    );
    this.setSection(bounds, [...(existing?.tagIds ?? []), ...tagIds]);
  }
  removeSection(id: string) {
    this.sections = this.sections.filter((s) => s.id !== id);
  }
  inRange(bounds: Bounds2D) {
    return this.sections.filter(
      ({ bounds: b }) =>
        b.left <= bounds.right &&
        b.right >= bounds.left &&
        b.top <= bounds.bottom &&
        b.bottom >= bounds.top,
    );
  }
}
